import Vue from "vue";
import api from "./api";
import { defaultContentForType, normalizePageContent, normalizePageType } from "./utils";

const SET_ACTIVE_PAGE = "SET_ACTIVE_PAGE";
const TRANSFER_PAGE = "TRANSFER_PAGE";
const UPDATE_PAGE = "UPDATE_PAGE";

const namespaced = true;

const state = {
  active: _getPage(),
  cache: _getPage(),
};

function _getPage() {
  return JSON.parse(localStorage.getItem("program_page") || "{}");
}

function leanPageForStorage(page) {
  if (!page || typeof page !== "object") return page || {};
  return {
    ...page,
    content: { html: "" },
  };
}

function pageContentFingerprint(page) {
  const html = page?.content?.html != null ? String(page.content.html) : "";
  return [
    page?.id || "",
    page?.type || "",
    page?.title || "",
    page?.internal_title || page?.internalTitle || "",
    html.length,
    html.slice(0, 48),
    html.slice(-48),
  ].join("|");
}

const mutations = {
  SET_ACTIVE_PAGE(state, data) {
    const page = data || {};
    const type = normalizePageType(page.type);
    const normalized = {
      ...page,
      type,
      content: normalizePageContent(type, page.content),
    };
    state.active = normalized;
    state.cache = { ...normalized, content: normalizePageContent(type, normalized.content) };
    // Never persist HTML — base64 images freeze the UI and inflate memory.
    try {
      localStorage.setItem("program_page", JSON.stringify(leanPageForStorage(state.active)));
    } catch (e) {
      // ignore quota / private mode
    }
  },
  TRANSFER_PAGE(state) {
    state.cache = {
      ...state.active,
      content: normalizePageContent(state.active.type, state.active.content),
    };
    try {
      localStorage.setItem("program_page", JSON.stringify(leanPageForStorage(state.cache)));
    } catch (e) {
      // ignore
    }
  },
  UPDATE_PAGE(state, { key, val }) {
    Vue.set(state.active, key, val);
  },
};

const actions = {
  setProgramPage({ commit }, data) {
    commit(SET_ACTIVE_PAGE, data);
  },
  setUpdate({ commit }, { key, val }) {
    commit(UPDATE_PAGE, { key, val });
  },

  getProgramPageList({ commit }, data) {
    return api
      .getProgramPageList(data)
      .then((data) => data.data)
      .catch((err) => {
        if (err) throw err.data;
      });
  },
  getProgramPageMetaList() {
    return api
      .getProgramPageMetaList()
      .then((data) => data.data)
      .catch((err) => {
        if (err) throw err.data;
      });
  },
  getProgramPageCount({ commit }, data) {
    return api
      .getProgramPageCount(data)
      .then((data) => Object.values(data.data)[0])
      .catch((err) => {
        if (err) throw err.data;
      });
  },
  getProgramPagesByIds({ commit }, ids) {
    return api
      .getProgramPagesByIds(ids)
      .then((data) => data.data)
      .catch((err) => {
        if (err) throw err.data;
      });
  },
  getOneProgramPage({ commit }, page_id) {
    return api
      .getOneProgramPage(page_id)
      .then((data) => {
        commit(SET_ACTIVE_PAGE, data.data);
        return data.data;
      })
      .catch((err) => {
        if (err) throw err.data;
      });
  },
  addProgramPage({ commit }, data) {
    const type = normalizePageType(data.type);
    const payload = {
      ...data,
      type,
      content: normalizePageContent(type, data.content || defaultContentForType()),
    };
    return api
      .addProgramPage(payload)
      .then((data) => data.data)
      .catch((err) => {
        if (err) throw err.data;
      });
  },
  updateProgramPage({ commit, state }, data) {
    var id = data ? data.page_id : state.active.id;
    var update = data ? data.data : state.active;
    const type = normalizePageType(update.type);
    const payload = {
      ...update,
      type,
      content: normalizePageContent(type, update.content),
    };

    return api
      .updateProgramPage(id, payload, state.cache)
      .then(() => {
        commit(TRANSFER_PAGE);
      })
      .catch((err) => {
        if (err) throw err.data;
      });
  },
  deleteProgramPage({ commit, state }, page_id) {
    var delete_id = page_id || state.active.id;
    return api
      .deleteProgramPage(delete_id)
      .then(() => commit(SET_ACTIVE_PAGE, {}))
      .catch((err) => {
        if (err) throw err.data;
      });
  },
};

const getters = {
  id: (state) => (state.active ? state.active.id : ""),
  active: (state) => state.active || {},
  cache: (state) => state.cache || {},

  hasChanges: (state) => {
    return pageContentFingerprint(state.active) !== pageContentFingerprint(state.cache);
  },
};

export default {
  namespaced,
  state,
  mutations,
  actions,
  getters,
};
